/**
 * Forgot Password Component
 * Handles password recovery for admin accounts
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import adminPasswordService from '../../lib/adminPasswordService';

const ForgotPassword = ({ onClose, onBack, onSuccess, defaultEmail = '' }) => {
  const [email, setEmail] = useState(defaultEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [step, setStep] = useState('email'); // 'email', 'sent', 'error'

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    if (!validateEmail(email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Use admin password service
      const result = await adminPasswordService.sendPasswordResetEmail(email);

      if (result.success) {
        setStep('sent');
        setMessage({ 
          type: 'success', 
          text: result.message
        });

        // Call success callback
        if (onSuccess) {
          onSuccess(email);
        }
      } else {
        throw new Error(result.message || 'Failed to send reset email');
      }

    } catch (error) {
      console.error('Password reset error:', error);
      setStep('error');
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to send reset email. Please try again or contact support.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Use admin password service
      const result = await adminPasswordService.sendPasswordResetEmail(email);

      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: 'Reset email sent again. Please check your inbox.' 
        });
      } else {
        throw new Error(result.message || 'Failed to resend email');
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to resend email. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
      <div className="bg-gray-900 rounded-xl border border-gray-700 p-8">
        {/* Header */}
        <div className="relative text-center mb-8">
          <button
            onClick={onClose}
            className="absolute top-0 right-0 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Forgot Password?</h2>
          <p className="text-gray-400">
            {step === 'email' 
              ? 'Enter your email address and we\'ll send you reset instructions.'
              : step === 'sent'
              ? 'Check your email for password reset instructions.'
              : 'There was an error sending the reset email.'
            }
          </p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' 
              ? 'bg-green-900/20 border border-green-700 text-green-400'
              : 'bg-red-900/20 border border-red-700 text-red-400'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {/* Email Form */}
        {step === 'email' && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email address"
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  <span>Send Reset Instructions</span>
                </>
              )}
            </button>
          </form>
        )}

        {/* Success State */}
        {step === 'sent' && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Email Sent!</h3>
              <p className="text-gray-400 text-sm">
                We've sent password reset instructions to <strong className="text-white">{email}</strong>
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleResendEmail}
                disabled={isLoading}
                className="w-full py-2 text-blue-400 hover:text-blue-300 text-sm disabled:opacity-50"
              >
                {isLoading ? 'Sending...' : 'Resend Email'}
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {step === 'error' && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-10 h-10 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Error Sending Email</h3>
              <p className="text-gray-400 text-sm">
                We couldn't send the reset email. Please try again or contact support.
              </p>
            </div>

            <button
              onClick={() => {
                setStep('email');
                setMessage({ type: '', text: '' });
              }}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Back to Login */}
        <div className="mt-8 pt-6 border-t border-gray-700">
          <button
            onClick={onBack}
            className="w-full flex items-center justify-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Login</span>
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Don't have access to your email?{' '}
            <button className="text-blue-400 hover:text-blue-300 underline">
              Contact Support
            </button>
          </p>
        </div>
      </div>
    </motion.div>
    </div>
  );
};

export default ForgotPassword;
